import { Link, useNavigate } from "@tanstack/react-router";
import { ImagePlus, Plus, Search } from "lucide-react";
import { type SubmitEvent, useEffect, useId, useMemo, useState } from "react";
import { toast } from "sonner";

import { useApi } from "../../app/api-context";
import { Pagination } from "../../components/data-table/pagination";
import { PageHeader } from "../../components/page-header";
import { ErrorAlert } from "../../components/ui/alert";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Field } from "../../components/ui/field";
import { Input, Select } from "../../components/ui/input";
import { Skeleton } from "../../components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { describeError, serverFieldError } from "../../lib/errors";
import { useDebouncedValue } from "../../lib/use-debounced-value";
import { useCurrentUser } from "../auth/session";
import { formatDate } from "../research/format";
import { describeCardStep } from "./card-format";
import { useCardList } from "./card-queries";

/** Research (D-64): one row per card, with how many listing links it has and where they are. */
export function CardsPage() {
  const [text, setText] = useState("");
  const [scope, setScope] = useState<"all" | "mine">("all");
  const [page, setPage] = useState(1);
  const [adding, setAdding] = useState(false);
  const q = useDebouncedValue(text.trim(), 300);
  const [closed, setClosed] = useState(false);
  const list = useCardList({ q, scope, closed, page });
  const { user } = useCurrentUser();
  const findId = useId();
  const showId = useId();

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Research"
        description="Cards you are researching. Open a card to add the listings you find online."
        actions={
          <Button onClick={() => setAdding(true)}>
            <Plus aria-hidden="true" />
            Add card
          </Button>
        }
      />

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex min-w-56 flex-1 flex-col gap-1 sm:max-w-sm">
          <label htmlFor={findId} className="text-xs font-medium text-muted-foreground">
            Find a card
          </label>
          <div className="relative">
            <Search
              className="pointer-events-none absolute start-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              id={findId}
              type="search"
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                setPage(1);
              }}
              placeholder="Card name"
              className="ps-8"
              autoComplete="off"
            />
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor={showId} className="text-xs font-medium text-muted-foreground">
            Show
          </label>
          <Select
            id={showId}
            value={scope}
            onChange={(e) => {
              setScope(e.target.value === "mine" ? "mine" : "all");
              setPage(1);
            }}
            className="w-48"
          >
            <option value="all">Everyone’s cards</option>
            <option value="mine">My cards</option>
          </Select>
        </div>
        <label className="flex items-center gap-2 pb-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={closed}
            onChange={(e) => {
              setClosed(e.target.checked);
              setPage(1);
            }}
            className="size-4 accent-primary"
          />
          Closed cards
        </label>
      </div>

      {list.isPending ? (
        <Card className="flex flex-col gap-3">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </Card>
      ) : list.isError ? (
        <ErrorAlert error={list.error} />
      ) : list.data.total === 0 ? (
        <Card className="py-12 text-center">
          <p className="text-sm text-muted-foreground">
            {q === "" && scope === "all" ? "No cards yet. Use “Add card” to start." : "No cards match."}
          </p>
        </Card>
      ) : (
        <>
          <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
            <Table className="min-w-[720px]">
              <caption className="sr-only">Cards in research</caption>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  {["Card name", "Collection", "Links", "Step", "Saved by", "Saved"].map((h) => (
                    <TableHead
                      key={h}
                      scope="col"
                      className="text-xs uppercase tracking-wide text-muted-foreground"
                    >
                      {h}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.data.data.map((card) => (
                  <TableRow key={card.id}>
                    <TableCell>
                      <Link
                        to="/cards/$cardId"
                        params={{ cardId: card.id }}
                        className="font-medium text-foreground hover:underline"
                      >
                        {card.name}
                      </Link>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{card.setName ?? "—"}</TableCell>
                    <TableCell>{card.linkCount}</TableCell>
                    <TableCell className="text-muted-foreground">{describeCardStep(card)}</TableCell>
                    <TableCell>{card.addedBy.id === user.id ? "You" : card.addedBy.name}</TableCell>
                    <TableCell className="text-muted-foreground">{formatDate(card.addedAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <Pagination
            page={list.data.page}
            pageSize={list.data.pageSize}
            total={list.data.total}
            busy={list.isPlaceholderData}
            onPage={setPage}
            noun="cards"
          />
        </>
      )}

      <AddCardDialog open={adding} onOpenChange={setAdding} />
    </div>
  );
}

/** "Add card": only the name, then straight to the card to add its listings. */
export function AddCardDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const api = useApi();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [setName_, setSetName] = useState("");
  const [image, setImage] = useState<File | undefined>(undefined);
  // A small preview of the chosen picture; the temporary address is released when it changes.
  const preview = useMemo(() => (image === undefined ? null : URL.createObjectURL(image)), [image]);
  useEffect(
    () => () => {
      if (preview !== null) URL.revokeObjectURL(preview);
    },
    [preview],
  );
  const setId = useId();
  const imageId = useId();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const nameId = useId();

  async function submit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    if (name.trim() === "" || busy) return;
    setBusy(true);
    setError(null);
    try {
      const { card } = await api.addCard({ name, setName: setName_ });
      // The picture is optional; a refused picture does not undo the card.
      if (image !== undefined)
        await api.setCardImage(card.id, image).catch((e: unknown) => toast.error(describeError(e).message));
      toast.success(`${card.name} added. Now add its listings.`);
      onOpenChange(false);
      setName("");
      setSetName("");
      setImage(undefined);
      await navigate({ to: "/cards/$cardId", params: { cardId: card.id } });
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  const fieldError =
    error === null ? undefined : (serverFieldError(error, "name") ?? describeError(error).message);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={(e) => void submit(e)} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>Add card</DialogTitle>
            <DialogDescription>Type the card name and number, e.g. Pikachu EX XY124.</DialogDescription>
          </DialogHeader>
          <Field id={nameId} label="Card name" error={fieldError}>
            <Input value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" autoFocus />
          </Field>
          <Field id={setId} label="Collection / set (optional)">
            <Input
              value={setName_}
              onChange={(e) => setSetName(e.target.value)}
              placeholder="e.g. Roaring Skies"
              autoComplete="off"
            />
          </Field>
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-foreground">Picture (optional)</span>
            {image === undefined ? (
              <label
                htmlFor={imageId}
                className="flex cursor-pointer flex-col items-center gap-1 rounded-lg border-2 border-dashed p-5 text-center transition-colors hover:border-ring hover:bg-accent/40"
              >
                <ImagePlus className="size-7 text-muted-foreground" aria-hidden="true" />
                <span className="text-sm font-medium text-foreground">
                  Click to add a picture of the card
                </span>
                <span className="text-xs text-muted-foreground">JPEG, PNG or WebP, up to 5 MB</span>
              </label>
            ) : (
              <div className="flex items-center gap-3 rounded-lg border p-3">
                <img src={preview ?? undefined} alt="" className="h-16 w-12 rounded object-cover" />
                <span className="min-w-0 flex-1 truncate text-sm">{image.name}</span>
                <label
                  htmlFor={imageId}
                  className="cursor-pointer text-sm font-medium text-primary hover:underline"
                >
                  Change
                </label>
                <Button variant="ghost" size="sm" onClick={() => setImage(undefined)}>
                  Remove
                </Button>
              </div>
            )}
            <input
              id={imageId}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              aria-label="Card picture"
              onChange={(e) => setImage(e.target.files?.[0])}
              className="sr-only"
            />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={name.trim() === "" || busy}>
              {busy ? "Adding…" : "Add card"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
